import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Wand2, Users, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLab, nameA, nameB, isSolo } from "@/context/LabContext";
import MicButton from "@/components/MicButton";

export default function IssueInputStep() {
  const { map, update, setStep } = useLab();
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [examples, setExamples] = useState([]);
  const solo = isSolo(map);

  useEffect(() => {
    api.get("/examples").then((r) => setExamples(r.data || [])).catch(() => setExamples([]));
  }, []);

  const applyExample = (ex) => {
    update({ issue: ex.issue, title: ex.issue, position_a: ex.position_a, position_b: ex.position_b });
    toast.success("Loaded a starting point — make it your own.");
  };

  const draftOpposing = async () => {
    if (!map.issue.trim() || !map.position_a.trim()) {
      toast.error("Add the disagreement and the first partner's view, then I can draft the other side.");
      return;
    }
    setDrafting(true);
    try {
      const { data } = await api.post("/ai/opposing-position", {
        issue: map.issue, position_a: map.position_a, name_a: nameA(map), name_b: nameB(map),
      });
      update({ position_b: map.position_b.trim() ? `${map.position_b.trim()}\n\n${data.opposing_position}` : data.opposing_position });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setDrafting(false);
  };

  const cont = async () => {
    if (!map.issue.trim() || !map.position_a.trim()) {
      toast.error("Please describe the disagreement and at least the first partner's view.");
      return;
    }
    if (!map.position_b.trim()) {
      setLoading(true);
      try {
        const { data } = await api.post("/ai/opposing-position", {
          issue: map.issue, position_a: map.position_a, name_a: nameA(map), name_b: nameB(map),
        });
        update({ position_b: data.opposing_position });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    setStep(1);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#2D2A26] mb-2">
        {solo ? "What are you working through?" : "What are the two of you working through?"}
      </h2>
      <p className="text-[#6E6860] mb-6">Take a breath. Describe the disagreement in your own words — there's no rush, and no judgment here.</p>

      {/* Mode selector: solo or together */}
      <div className="mb-8" data-testid="mode-selector">
        <p className="text-sm font-medium text-[#2D2A26] mb-2">Who's doing this right now?</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <ModeCard
            active={!solo}
            onClick={() => update({ mode: "together" })}
            icon={Users}
            title="Together"
            body="We're both here. Each of us will speak for ourselves."
            testId="mode-together"
          />
          <ModeCard
            active={solo}
            onClick={() => update({ mode: "solo" })}
            icon={User}
            title="On my own"
            body="Just me for now. I'll describe both sides — and AI can help me guess my partner's."
            testId="mode-solo"
          />
        </div>
      </div>

      {/* Partner names */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <Block label={solo ? "Your name" : "First partner's name"}>
          <input
            value={map.partner_a_name}
            onChange={(e) => update({ partner_a_name: e.target.value })}
            placeholder="e.g. Alex"
            data-testid="partner-a-name-input"
            className="w-full rounded-xl border border-[#E8E3D9] bg-white px-4 py-3 text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition"
          />
        </Block>
        <Block label={solo ? "Your partner's name" : "Second partner's name"}>
          <input
            value={map.partner_b_name}
            onChange={(e) => update({ partner_b_name: e.target.value })}
            placeholder="e.g. Sam"
            data-testid="partner-b-name-input"
            className="w-full rounded-xl border border-[#E8E3D9] bg-white px-4 py-3 text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition"
          />
        </Block>
      </div>

      <Block label="What's the disagreement about?">
        <div className="relative">
          <input
            value={map.issue}
            onChange={(e) => update({ issue: e.target.value })}
            placeholder="e.g. Sharing the housework, money, time with family…"
            data-testid="issue-input"
            className="w-full rounded-xl border border-[#E8E3D9] bg-white px-4 py-3 pr-12 text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition"
          />
          <MicButton value={map.issue} onChange={(v) => update({ issue: v })} className="absolute right-2 top-1/2 -translate-y-1/2" testId="issue-mic" />
        </div>
      </Block>

      {examples.length > 0 && (
        <div className="mt-4" data-testid="example-starters">
          <p className="text-sm text-[#6E6860] mb-2">Not sure how to start? Try a common one — you can edit everything:</p>
          <div className="flex flex-wrap gap-2">
            {examples.map((ex, i) => (
              <button
                key={ex.key}
                onClick={() => applyExample(ex)}
                data-testid={`example-chip-${i}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-[#F5F2EA] text-[#2D2A26] border border-[#E8E3D9] hover:bg-[#D9C5B2]/40 transition-all"
              >
                <Wand2 className="h-3.5 w-3.5 text-[#4A5D4E]" /> {ex.issue}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 space-y-6">
        <Block label={solo ? "How do you see it?" : `How does ${nameA(map)} see it?`} hint={solo ? undefined : `${nameA(map)}, describe your own view in your words.`}>
          <div className="relative">
            <textarea
              value={map.position_a}
              onChange={(e) => update({ position_a: e.target.value })}
              placeholder="Describe this partner's view, in their words…"
              rows={3}
              data-testid="position-a-input"
              className="w-full rounded-xl border border-[#E8E3D9] bg-white px-4 py-3 pr-12 text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition resize-none"
            />
            <MicButton value={map.position_a} onChange={(v) => update({ position_a: v })} className="absolute bottom-2.5 right-2.5" testId="position-a-mic" />
          </div>
        </Block>

        <Block
          label={solo ? `How do you think ${nameB(map)} sees it?` : `How does ${nameB(map)} see it?`}
          hint={
            solo
              ? `Take your best guess at ${nameB(map)}'s view — or leave it blank and let AI draft a fair version for you.`
              : `${nameB(map)}, describe your own view. (Or leave blank and let AI draft a fair version.)`
          }
        >
          <div className="relative">
            <textarea
              value={map.position_b}
              onChange={(e) => update({ position_b: e.target.value })}
              placeholder={solo ? "Your best guess — or leave blank for AI…" : "Optional…"}
              rows={3}
              data-testid="position-b-input"
              className="w-full rounded-xl border border-[#E8E3D9] bg-white px-4 py-3 pr-12 text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition resize-none"
            />
            <MicButton value={map.position_b} onChange={(v) => update({ position_b: v })} className="absolute bottom-2.5 right-2.5" testId="position-b-mic" />
          </div>
          <button
            onClick={draftOpposing}
            disabled={drafting}
            data-testid="draft-opposing-button"
            className="mt-3 inline-flex items-center gap-2 text-sm text-[#4A5D4E] hover:text-[#3B4A3E] underline underline-offset-4 disabled:opacity-60"
          >
            <Sparkles className={`h-4 w-4 ${drafting ? "animate-pulse" : ""}`} />
            {drafting ? "Drafting…" : map.position_b.trim() ? `Let AI draft ${nameB(map)}'s view instead` : `Let AI guess ${nameB(map)}'s view`}
          </button>
        </Block>
      </div>

      <div className="mt-10 flex justify-end">
        <button
          onClick={cont}
          disabled={loading}
          data-testid="issue-continue-button"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#4A5D4E] text-[#FDFBF7] font-medium hover:bg-[#3B4A3E] transition-all hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading ? (
            <>
              <Sparkles className="h-4 w-4 animate-pulse" /> Drafting the other view…
            </>
          ) : (
            <>
              Continue <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}

function Block({ label, hint, children }) {
  return (
    <div>
      <label className="block text-base font-medium text-[#2D2A26] mb-1.5">{label}</label>
      {hint && <p className="text-sm text-[#6E6860] mb-2">{hint}</p>}
      {children}
    </div>
  );
}

function ModeCard({ active, onClick, icon: Icon, title, body, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`text-left rounded-2xl border p-4 transition-all ${
        active
          ? "border-[#4A5D4E] bg-[#4A5D4E]/5 ring-1 ring-[#4A5D4E]/30"
          : "border-[#E8E3D9] bg-white hover:border-[#4A5D4E]/40"
      }`}
    >
      <span className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${active ? "text-[#4A5D4E]" : "text-[#6E6860]"}`} strokeWidth={1.75} />
        <span className={`font-medium ${active ? "text-[#2D2A26]" : "text-[#2D2A26]"}`}>{title}</span>
      </span>
      <span className="block text-sm text-[#6E6860] leading-relaxed">{body}</span>
    </button>
  );
}
